import { useTranslation } from 'react-i18next';
import type { DatePreset } from '../hooks/useDateRange.js';

/** @graph-connects none */
const PRESETS: { key: Exclude<DatePreset, 'custom'>; labelKey: string }[] = [
  { key: '7d', labelKey: 'common.last7days' },
  { key: '30d', labelKey: 'common.last30days' },
  { key: '90d', labelKey: 'common.last90days' },
];

interface DateRangeSelectorProps {
  preset: DatePreset;
  startDate: string;
  endDate: string;
  onPresetChange: (p: DatePreset) => void;
  onCustomDatesChange: (start: string, end: string) => void;
}

/**
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business 日付範囲セレクター。7日/30日/90日プリセットボタンとカスタム日付ピッカーで分析期間を指定
 * @graph-connects none
 */
export function DateRangeSelector({
  preset,
  startDate,
  endDate,
  onPresetChange,
  onCustomDatesChange,
}: DateRangeSelectorProps) {
  const { t } = useTranslation();

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {/* プリセットボタン */}
      <div className="flex gap-1 p-1 rounded-lg bg-[var(--color-bg-hover)]">
        {PRESETS.map((p) => (
          <button
            key={p.key}
            onClick={() => {
              onPresetChange(p.key);
            }}
            className={`
              px-3 py-1.5 rounded-md text-xs font-medium transition-colors
              ${
                preset === p.key
                  ? 'bg-[var(--color-accent-primary)] text-white shadow-sm'
                  : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'
              }
            `}
          >
            {t(p.labelKey)}
          </button>
        ))}
      </div>

      {/* カスタム日付入力 */}
      <div className="flex items-center gap-1.5">
        <input
          type="date"
          value={startDate}
          onChange={(e) => {
            onCustomDatesChange(e.target.value, endDate);
          }}
          className={`
            px-2 py-1.5 rounded-md text-xs border transition-colors
            bg-[var(--color-bg-hover)] text-[var(--color-text-primary)]
            border-[var(--color-border)] focus:border-[var(--color-border-focus)] focus:outline-none
            ${preset === 'custom' ? 'border-[var(--color-accent-primary)]' : ''}
          `}
        />
        <span className="text-xs text-[var(--color-text-muted)]">–</span>
        <input
          type="date"
          value={endDate}
          onChange={(e) => {
            onCustomDatesChange(startDate, e.target.value);
          }}
          className={`
            px-2 py-1.5 rounded-md text-xs border transition-colors
            bg-[var(--color-bg-hover)] text-[var(--color-text-primary)]
            border-[var(--color-border)] focus:border-[var(--color-border-focus)] focus:outline-none
            ${preset === 'custom' ? 'border-[var(--color-accent-primary)]' : ''}
          `}
        />
      </div>
    </div>
  );
}
